import * as React from "react";
import { useCallback, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOrganizationexecuteTokenOperationMutation, useOrganizationvalidateTokenOperationQuery } from "api";
import { isBioErrorResponse, isFetchBaseQueryError } from "api/types";
import { Formik } from "formik";
import { isNil } from "ramda";

import { NavigateButton } from "affinityUI/elements/button";
import { Grid } from "affinityUI/elements/grid";
import { Layout } from "affinityUI/structures/layout";
import { useExhPasswordReset } from "lib/extrahorizon/hooks";
import { IdleHandler } from "lib/inactivityHandler/idleHandler";
import { Yup } from "lib/yup/extendYup";

import { PasswordForm } from "./passwordForm";

const handleIdle = () => {
  // Removes all search params from the URL
  window.location.assign(window.location.pathname);
};

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[0-9]/, "Password must contain at least 1 number")
    .matches(/[a-z]/, "Password must contain a lowercase letter")
    .matches(/[A-Z]/, "Password must contain an uppercase letter")
    .matches(/[^A-Za-z0-9]/, "Password must contain at least 1 symbol"),
});

export const SetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { showBoundary: onError } = useErrorBoundary();
  const navigate = useNavigate();

  const token = searchParams.get("token") || "";
  const operation = searchParams.get("operation") || "";
  const entityId = searchParams.get("entityId") || "";
  // Seems that BioT is not url-encoding the username correctly, as + in the username is 'correctly' decoded to a space
  // This is a simple workaround to replace the space with a + to get the correct username...
  const username = (searchParams.get("username") || "").replace(" ", "+");
  const hash = searchParams.get("hash") || "";

  const isAcceptInvite = useMemo(() => operation === "accept-invitation", [operation]);

  const {
    isFetching: orgIsFetching,
    isSuccess: orgIsSuccess,
    isError: orgIsError,
  } = useOrganizationvalidateTokenOperationQuery(
    {
      token,
      operation,
      entityId,
    },
    {
      skip: isNil(token) || !isAcceptInvite,
    }
  );

  const [orgUpdatePassword] = useOrganizationexecuteTokenOperationMutation();
  const { validatePasswordReset } = useExhPasswordReset();

  const handleSubmit = useCallback(
    async (formData, { setErrors }) => {
      try {
        if (isAcceptInvite) {
          await orgUpdatePassword({
            token,
            operation,
            entityId,
            body: { operationData: { ...formData, ...(username ? { username } : {}) } },
          }).unwrap();
        } else {
          await validatePasswordReset({ hash, newPassword: formData.password });
        }
        navigate("success");
      } catch (error) {
        if (isFetchBaseQueryError(error) && isBioErrorResponse(error.data)) {
          setErrors(error.data);
        } else {
          onError(error);
        }
      }
    },
    [
      isAcceptInvite,
      orgUpdatePassword,
      validatePasswordReset,
      token,
      operation,
      entityId,
      username,
      hash,
      navigate,
      onError,
    ]
  );

  const exhFormReady = !isAcceptInvite && Boolean(hash);
  const exhLinkInvalid = !isAcceptInvite && !hash;
  const showForm = isAcceptInvite ? orgIsSuccess : exhFormReady;
  const showLinkError = isAcceptInvite ? orgIsError : exhLinkInvalid;

  return (
    <Layout title={isAcceptInvite ? "Create your password" : "New Password"} publicLayout loading={orgIsFetching}>
      {showForm ? (
        <>
          <IdleHandler timeout={5 * 60 * 1000} onIdle={handleIdle} />
          <Formik
            enableReinitialize
            validateOnBlur
            validateOnChange
            validationSchema={passwordSchema}
            initialValues={{}}
            onSubmit={handleSubmit}
          >
            <PasswordForm />
          </Formik>
        </>
      ) : null}
      {isAcceptInvite && isNil(token) && (
        <Grid>
          <div>
            <p>Form is timed out. It likely happened because of user inactivity.</p>
            <p>Please reopen link in received email to try again.</p>
          </div>
        </Grid>
      )}
      {showLinkError ? (
        <Grid>
          <div>There is a problem with your link.</div>
          {!isAcceptInvite ? (
            <NavigateButton to={"../../recover"}>Resend password recovery email</NavigateButton>
          ) : null}
        </Grid>
      ) : null}
    </Layout>
  );
};
